import { useState } from 'react';

import { PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openNewPlaylistDialog,
  openPlaylistViewStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { Box, SxProps, Theme } from '@mui/material';

import { PlaylistCard } from './PlaylistCard';
import { useAgentContext } from './PlaylistSection.agentContext';
import { PlaylistSectionEmptyState } from './PlaylistSectionEmptyState';
import { SectionHeader } from '../atoms/SectionHeader';
import { PLAYLIST_CARD_LIST_VIEW_WIDTH } from '../constants/priorities.constants';
import { handlePlaylistSearch } from '../utils/playlist.utils';

const PLAYLIST_SECTION_STRINGS = appStrings.modules.priorities.playlistsSection;

type PlaylistSectionProps = Readonly<{
  playlists: PriorityPlaylist[];
}>;

export const PlaylistSection = ({ playlists }: PlaylistSectionProps) => {
  useAgentContext(playlists);
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => state.user.user);

  const [searchTerm, setSearchTerm] = useState('');

  const handlePlaylistClick = (playlist: PriorityPlaylist) => {
    dispatch(openPlaylistViewStep(playlist));
  };

  const handleOpeningNewPlaylistDialog = (preLoadedTitle?: string) => {
    dispatch(openNewPlaylistDialog(preLoadedTitle ? { preLoadedTitle } : undefined));
  };

  const filteredPlaylists = handlePlaylistSearch(playlists, searchTerm);

  return (
    <Box sx={containerSx}>
      <SectionHeader
        title={PLAYLIST_SECTION_STRINGS.title}
        description={PLAYLIST_SECTION_STRINGS.description}
        searchPlaceholder={PLAYLIST_SECTION_STRINGS.searchPlaceholder}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isFilteredResultEmpty={filteredPlaylists.length === 0 && playlists.length > 0}
        disabled={playlists.length === 0}
        inline
      />

      {/* WITH PLAYLISTS */}
      <Box sx={playlistsContainerSx}>
        {filteredPlaylists.map((playlist) => (
          <PlaylistCard
            key={playlist.id}
            playlist={playlist}
            onClick={() => handlePlaylistClick(playlist)}
            playlistCardWidth={PLAYLIST_CARD_LIST_VIEW_WIDTH}
          />
        ))}

        {/* EMPTY STATE */}
        {(!playlists || playlists.length === 0) && (
          <PlaylistSectionEmptyState
            openNewPlaylistDialog={handleOpeningNewPlaylistDialog}
            currentUser={currentUser}
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  maxWidth: '1240px',
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
  px: backrs.spacing['spacing-none'],
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-3xl'],
  },
});

const playlistsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
  gap: backrs.spacing['spacing-2xl'],
});
